import {
  Card,
  CardBody,
  CardCell,
  CardFooter,
  CardHeader,
  CardHeaderContent,
  Icon,
  InfoRow,
  Typography
} from '@sollapay/ui/components';
import {formatDateLocalized, formatNIS} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {useTrustAccountPermissions} from '@/features/trust-accounts/providers';

import {
  canDeleteBuyerRow,
  formatPurchaseAssetNames,
  formatBuyerDisplayName,
  isOrganizationBuyer
} from '../utils';
import {BuyerActionButton} from './BuyerActionButton';
import {BuyerAvatarStack} from './BuyerAvatarStack';
import {BuyerMoreMenu} from './BuyerMoreMenu';
import {BuyerPaymentStatusBadge} from './BuyerPaymentStatusBadge';
import {BuyerRegulatoryReportsBadge} from './BuyerRegulatoryReportsBadge';

import type {BuyerTableRow} from '../types/buyers.types';
import type {BuyerDTO, BuyerPurchaseDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface BuyerCardProps {
  row: BuyerTableRow;
  onRequestPayment: (buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => void;
  onViewBuyer: (buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => void;
  onEditBuyer: (buyers: BuyerDTO[]) => void;
  onDeleteBuyer: (buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => void;
  trustAccountId: string;
}

export const BuyerCard: FC<BuyerCardProps> = ({
  row,
  onRequestPayment,
  onViewBuyer,
  onEditBuyer,
  onDeleteBuyer,
  trustAccountId
}) => {
  const {t, i18n} = useTranslation('trustAccounts');
  const {canUpdateBuyer, canDeleteBuyer, canCollectPayments} = useTrustAccountPermissions();
  const {buyers, purchase, depositPaidNis, paymentStatus} = row;
  const primaryBuyer = buyers[0];
  const displayName = formatBuyerDisplayName(primaryBuyer.fullName);
  const assetLabel = formatPurchaseAssetNames(purchase.assets, t);
  const canDeleteThisRow = canDeleteBuyerRow(buyers, canDeleteBuyer);

  return (
    <Card
      className="transition-colors duration-300 ease-in-out hover:border-brand-primary"
      onCardClick={() => onViewBuyer(primaryBuyer, purchase)}
    >
      <CardHeader>
        <CardHeaderContent>
          {buyers.length === 1 ? (
            <>
              <BuyerAvatarStack
                buyers={buyers}
                assetLabel={assetLabel}
                onViewBuyer={buyer => onViewBuyer(buyer, purchase)}
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {isOrganizationBuyer(primaryBuyer.partyType) && (
                    <Icon
                      name="building"
                      width={16}
                      height={16}
                      className="shrink-0 text-fg-tertiary"
                      aria-label={t('view.buyers.list.corporateBadge')}
                    />
                  )}
                  <Typography as="h3" size="sm" weight="medium">
                    {displayName}
                  </Typography>
                </div>
                <Typography size="xs" color="tertiary">
                  {assetLabel}
                </Typography>
              </div>
            </>
          ) : (
            <BuyerAvatarStack
              buyers={buyers}
              assetLabel={assetLabel}
              layout="stacked"
              onViewBuyer={buyer => onViewBuyer(buyer, purchase)}
            />
          )}
        </CardHeaderContent>
        <div
          role="presentation"
          className="flex items-center gap-1"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <BuyerRegulatoryReportsBadge purchase={purchase} trustAccountId={trustAccountId} />
          <BuyerMoreMenu
            actions={canDeleteThisRow ? ['edit', 'view', 'delete'] : ['edit', 'view']}
            orientation="vertical"
            variant="bordered"
            onView={() => onViewBuyer(primaryBuyer, purchase)}
            onEdit={canUpdateBuyer ? () => onEditBuyer(buyers) : undefined}
            onDelete={canDeleteThisRow ? () => onDeleteBuyer(primaryBuyer, purchase) : undefined}
          />
        </div>
      </CardHeader>

      <CardBody className="grid flex-1 grid-cols-2 gap-x-3 gap-y-4">
        <CardCell>
          <InfoRow
            label={t('view.buyers.list.columns.purchasePrice')}
            value={formatNIS(purchase.purchasePriceNis)}
            layout="vertical"
          />
        </CardCell>

        <CardCell>
          <InfoRow
            label={t('view.buyers.list.columns.depositPaid')}
            value={formatNIS(depositPaidNis)}
            layout="vertical"
          />
        </CardCell>

        <CardCell>
          <InfoRow
            label={t('view.buyers.list.columns.createdAt')}
            value={formatDateLocalized(primaryBuyer.createdAt, i18n.language)}
            layout="vertical"
          />
        </CardCell>

        <CardCell>
          <Typography size="xs" color="tertiary">
            {t('view.buyers.list.columns.paymentStatus')}
          </Typography>
          <div className="mt-1 flex flex-col items-start">
            <BuyerPaymentStatusBadge status={paymentStatus} />
          </div>
        </CardCell>
      </CardBody>

      <CardFooter
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <BuyerActionButton
          showRequestPayment={canCollectPayments && row.canRequestPayment}
          onRequestPayment={() => onRequestPayment(primaryBuyer, purchase)}
          onViewBuyer={() => onViewBuyer(primaryBuyer, purchase)}
          className="w-full"
        />
      </CardFooter>
    </Card>
  );
};
